import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { useAuth } from '../../../lib/auth/AuthContext';

const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

// Company logo
let companyLogo: string | null = null;
try {
  companyLogo = require('../../../assets/images/CRCEvansLogo.png');
} catch (e) {
  // Logo not found
}

interface NavigationItem {
  name: string;
  href: string;
  description?: string;
  icon: React.FC<{ className?: string }>;
}

const ProposalsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ConfigurationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const AnalyticsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const SpreadsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM13 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2V6zM4 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3zM13 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2v-3z" />
  </svg>
);

const FaqIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const navigationItems: NavigationItem[] = [
  { name: 'Proposals', href: '/proposals', icon: ProposalsIcon, description: 'Manage sales proposals' },
  // TEMPORARY link to the spread-split prototype; rename/route when it graduates out of /prototype.
  { name: 'Spread Proposal', href: '/prototype/spread-split', icon: SpreadsIcon, description: 'Spread-level proposal (preview)' },
  { name: 'Configuration', href: '/configuration', icon: ConfigurationIcon, description: 'Pricing configuration' },
  { name: 'FAQ', href: '/faq', icon: FaqIcon, description: 'Commercial FAQ' },
  // Dashboard and Analytics are hidden until enough data is available to build them out
  // { name: 'Dashboard', href: '/dashboard', icon: DashboardIcon, description: 'Overview and key metrics' },
  // { name: 'Analytics', href: '/analytics', icon: AnalyticsIcon, description: 'Reports and insights' },
];

export const Navigation: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const initials = (user?.displayName || user?.email || 'U')
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside
      className="flex flex-col bg-blueprint-blue h-full flex-shrink-0"
      style={{ width: 240 }}
    >
      {/* Brand header */}
      <div className="h-[60px] flex items-center px-5 border-b border-white/10 gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-hero-red flex items-center justify-center flex-shrink-0">
          <AnalyticsIcon className="w-[18px] h-[18px] text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-semibold text-[14px] text-white">
            <span className="text-hero-red-30">DATA</span> 360
          </div>
          <div className="text-[11px] text-white/40">Sales Toolkit</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 flex flex-col gap-1 overflow-y-auto">
        <div className="px-3 pt-2 pb-2 text-[10px] font-semibold text-white/35 uppercase tracking-[0.1em]">
          Workspace
        </div>
        {navigationItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150',
                isActive
                  ? 'bg-hero-red/15 text-white'
                  : 'text-white/50 hover:text-white/80 hover:bg-white/5'
              )
            }
          >
            <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      {/* User profile footer */}
      <div className="border-t border-white/10 flex-shrink-0">
        {user && (
          <div className="px-3 py-3">
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-hero-red flex items-center justify-center flex-shrink-0">
                <span className="text-[12px] font-semibold text-white font-heading">{initials}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-white truncate">
                  {user.displayName}
                </div>
                <div className="text-[11px] text-white/40 truncate">
                  {user.role || user.email}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logout */}
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-hero-red-30 hover:bg-white/5 transition-all duration-150 w-full text-[13px] font-medium"
          >
            <LogoutIcon className="h-4 w-4" />
            <span>Log out</span>
          </button>
        </div>

        {/* Version + branding */}
        <div className="px-4 py-3 border-t border-white/10">
          <div className="text-[10px] text-white/25 text-center mb-2">v{APP_VERSION}</div>
          <p className="text-[9px] font-medium text-white/25 text-center mb-1.5 uppercase tracking-wider">
            Powered by
          </p>
          {companyLogo ? (
            <img
              src={companyLogo}
              alt="CRC Evans"
              className="h-6 w-auto mx-auto object-contain"
            />
          ) : (
            <p className="text-[11px] font-semibold text-white/40 text-center">CRC Evans</p>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Navigation;
